import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import MainCalculatorsList from "./MainCalculators";
const unitTranslations = {
  kg: "كيلوغرام",
  g: "غرام",
  mg: "ميليغرام",
  metricton: "طن متري",
  longton: "طن طويل",
  shortton: "طن قصير",
  pound: "رطل",
  ounce: "أوقية",
  carat: "قيراط",
  amu: "وحدة كتلة ذرية",
};
const unitSymbols = {
  kg: "كغ",
  g: "غ",
  mg: "ملغ",
  metricton: "طن",
  longton: "طن",
  shortton: "طن",
  pound: "lb",
  ounce: "oz",
  carat: "ct",
  amu: "amu",
};
const conversionRates = {
  kg: {
    kg: 1,
    g: 1000,
    mg: 1e6,
    metricton: 0.001,
    longton: 0.000984207,
    shortton: 0.00110231,
    pound: 2.20462,
    ounce: 35.274,
    carat: 5000,
    amu: 6.022e26,
  },
  g: {
    kg: 0.001,
    g: 1,
    mg: 1000,
    metricton: 1e-6,
    longton: 9.84207e-7,
    shortton: 1.10231e-6,
    pound: 0.00220462,
    ounce: 0.035274,
    carat: 5,
    amu: 6.022e23,
  },
  mg: {
    kg: 1e-6,
    g: 0.001,
    mg: 1,
    metricton: 1e-9,
    longton: 9.84207e-10,
    shortton: 1.10231e-9,
    pound: 2.20462e-6,
    ounce: 3.5274e-5,
    carat: 0.005,
    amu: 6.022e20,
  },
  metricton: {
    kg: 1000,
    g: 1e6,
    mg: 1e9,
    metricton: 1,
    longton: 0.984207,
    shortton: 1.10231,
    pound: 2204.62,
    ounce: 35274,
    carat: 5e6,
    amu: 6.022e29,
  },
  longton: {
    kg: 1016.05,
    g: 1.01605e6,
    mg: 1.01605e9,
    metricton: 1.01605,
    longton: 1,
    shortton: 1.12,
    pound: 2240,
    ounce: 35840,
    carat: 5.08023e6,
    amu: 6.12293e29,
  },
  shortton: {
    kg: 907.185,
    g: 9.07185e5,
    mg: 9.07185e8,
    metricton: 0.907185,
    longton: 0.892857,
    shortton: 1,
    pound: 2000,
    ounce: 32000,
    carat: 4.53592e6,
    amu: 5.46319e29,
  },
  pound: {
    kg: 0.453592,
    g: 453.592,
    mg: 453592,
    metricton: 0.000453592,
    longton: 0.000446429,
    shortton: 0.0005,
    pound: 1,
    ounce: 16,
    carat: 2267.96,
    amu: 2.73159e26,
  },
  ounce: {
    kg: 0.0283495,
    g: 28.3495,
    mg: 28349.5,
    metricton: 2.835e-5,
    longton: 2.7902e-5,
    shortton: 3.125e-5,
    pound: 0.0625,
    ounce: 1,
    carat: 141.748,
    amu: 1.70724e25,
  },
  carat: {
    kg: 0.0002,
    g: 0.2,
    mg: 200,
    metricton: 2e-7,
    longton: 1.96841e-7,
    shortton: 2.20462e-7,
    pound: 0.000440925,
    ounce: 0.00705479,
    carat: 1,
    amu: 1.2044e23,
  },
  amu: {
    kg: 1.66054e-27,
    g: 1.66054e-24,
    mg: 1.66054e-21,
    metricton: 1.66054e-30,
    longton: 1.63523e-30,
    shortton: 1.83237e-30,
    pound: 3.66086e-27,
    ounce: 5.85732e-26,
    carat: 8.3027e-24,
    amu: 1,
  },
};
function WeightConverter() {
  const { fromWeightUnit, toWeightUnit } = useParams();
  const [amount, setAmount] = useState("");
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [rate, setRate] = useState(null);
  const translatedFromUnit = unitTranslations[fromWeightUnit] || fromWeightUnit;
  const translatedToUnit = unitTranslations[toWeightUnit] || toWeightUnit;
  const TITLE = `تحويل من ${translatedFromUnit} إلى ${translatedToUnit} - محول دقيق وسريع`;
  const DESCRIPTION = `محول وحدات الوزن الدقيق لتحويل ${translatedFromUnit} إلى ${translatedToUnit}. احسب وحوّل ${translatedFromUnit} إلى ${translatedToUnit} بسهولة مع جدول تحويل شامل وشرح مفصل. أداة مجانية وسريعة مع معادلة التحويل وأمثلة عملية.`;
  const KEYWORDS = `تحويل ${translatedFromUnit} إلى ${translatedToUnit}, ${translatedFromUnit} إلى ${translatedToUnit}, محول ${translatedFromUnit}, حاسبة ${translatedFromUnit} ${translatedToUnit}, ${fromWeightUnit} to ${toWeightUnit}, جدول تحويل ${translatedFromUnit}, كم ${translatedToUnit} في ${translatedFromUnit}`;
  const OG_URL = `https://do-calculate.com/calculator/ar/unit_converter/weight/${fromWeightUnit}/${toWeightUnit}/`;
  useEffect(() => {
    if (
      conversionRates[fromWeightUnit] &&
      conversionRates[fromWeightUnit][toWeightUnit]
    ) {
      setRate(conversionRates[fromWeightUnit][toWeightUnit]);
    } else {
      setRate(null);
    }
  }, [fromWeightUnit, toWeightUnit]);
  useEffect(() => {
    if (amount && !isNaN(amount) && rate) {
      setConvertedAmount(Number(amount) * rate);
    } else {
      setConvertedAmount(null);
    }
  }, [amount, rate]);
  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };
  const handleReset = () => {
    setAmount("");
    setConvertedAmount(null);
  };
  const amounts = [1, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];
  const conversionTable = amounts.map((a) => ({
    fromAmount: a,
    toAmount: rate ? a * rate : 0,
  }));
  // Format large numbers without scientific notation
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) {
      return "0";
    }
    return num.toLocaleString(undefined, {
      maximumFractionDigits: 10,
      useGrouping: true
    });
  };
  // Generate Schema.org structured data
  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "الصفحة الرئيسية",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "محولات الوحدات",
          item: "https://do-calculate.com/ar/unit_converter/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "تحويل الوزن",
          item: "https://do-calculate.com/ar/unit_converter/weight/",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: `${translatedFromUnit} إلى ${translatedToUnit}`,
          item: OG_URL,
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: `محول ${translatedFromUnit} إلى ${translatedToUnit}`,
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "UtilityApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, calculatorSchema];
  };
  // Get conversion formula explanation
  const getFormulaExplanation = () => {
    if (!rate || rate === null) {
      return "جاري تحميل معامل التحويل...";
    }
    if (rate === 1) {
      return `${translatedFromUnit} و${translatedToUnit} هما نفس الوحدة، لذا القيمة تبقى كما هي.`;
    } else if (rate > 1) {
      return `لتحويل ${translatedFromUnit} إلى ${translatedToUnit}، اضرب القيمة في ${formatNumber(
        rate
      )}.`;
    } else {
      return `لتحويل ${translatedFromUnit} إلى ${translatedToUnit}، اضرب القيمة في ${formatNumber(
        rate
      )} (أو اقسم على ${formatNumber(1 / rate)}).`;
    }
  };
  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>
                  محول {translatedFromUnit} إلى {translatedToUnit}
                </strong>{" "}
                هو أداة دقيقة وسريعة لتحويل وحدات الوزن. حوّل{" "}
                <strong>{translatedFromUnit}</strong> إلى{" "}
                <strong>{translatedToUnit}</strong> بكل سهولة مع معادلة التحويل
                وجدول شامل للقيم الشائعة.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <hr />
            <div className="row">
             <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    {translatedFromUnit} → {translatedToUnit}
                  </h2>
                </div>
                <div
                  className="alert alert-info"
                  style={{ fontSize: "15px", lineHeight: "1.8" }}
                >
                  <strong>معادلة التحويل:</strong>
                  <br />1 {translatedFromUnit} = {rate ? formatNumber(rate) : "..."}{" "}
                  {translatedToUnit}
                  <br/>                  {getFormulaExplanation()}
                </div>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group mb-3">
                    <label  htmlFor="amount">
                      <strong>أدخل القيمة بالـ {translatedFromUnit}:</strong>
                    </label>
                    <input
                      id="amount"
                      type="number"
                      className="form-control"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder={`مثال: 10`}
                      step="any"
                      style={{ fontSize: "16px", padding: "10px" }}
                    />
                  </div>
                  <hr />
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                      style={{ fontSize: "16px", padding: "10px 20px" }}
                    >
                      امسح
                    </button>
                    <Link
                      to={`/calculator/ar/unit_converter/weight/${toWeightUnit}/${fromWeightUnit}/`}
                    >
                      <button
                        className="btn btn-outline-success"
                        style={{ fontSize: "15px", padding: "10px 15px" }}
                      >
                        🔄 عكس التحويل ({translatedToUnit} → {translatedFromUnit}
                        )
                      </button>
                    </Link>
                  </div>
                </form>
              </div>
              <div className="col-sm-12 col-md-4">
                {convertedAmount !== null && amount && (
                  <div className="calculatorStyle">
                    <div className="p-3 mb-3 bg-success text-white text-center rounded">
                      <h3 style={{ fontSize: "18px", margin: 0 }}>النتيجة</h3>
                    </div>
                    <div
                      className="alert alert-success"
                      style={{ fontSize: "16px", lineHeight: "1.8" }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          backgroundColor: "#d4edda",
                          borderRadius: "8px",
                          marginBottom: "15px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "18px",
                            marginBottom: "10px",
                            color: "#155724",
                          }}
                        >
                          <strong>
                            {Number(amount).toLocaleString()} {translatedFromUnit}
                          </strong>
                        </div>
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: "#155724",
                          }}
                        >
                          = {formatNumber(convertedAmount)} {translatedToUnit}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          marginTop: "15px",
                          backgroundColor: "#fff",
                          padding: "15px",
                          borderRadius: "8px",
                        }}
                      >
                        <strong>طريقة الحساب:</strong>
                        <div style={{ marginTop: "10px", lineHeight: "1.8" }}>
                          {Number(amount).toLocaleString()} × {formatNumber(rate)}
                          <br />= <strong>{formatNumber(convertedAmount)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!amount && (
                  <div className="alert alert-warning" role="alert">
                    <strong>ملاحظة:</strong> أدخل القيمة لرؤية النتيجة
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div>
              <h3 style={{ fontSize: "18px", marginBottom: "15px" }}>
                جدول التحويل من {translatedFromUnit} إلى {translatedToUnit}
              </h3>
              <p
                className="alert alert-success"
                style={{ fontSize: "15px", lineHeight: "1.8" }}
              >
                كل <strong>1 {translatedFromUnit}</strong> يساوي{" "}
                <strong>{rate ? formatNumber(rate) : "..."} {translatedToUnit}</strong>. الجدول
                التالي يوضح التحويل للقيم الشائعة:
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#e7f3fe" }}>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        {translatedFromUnit}
                      </th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        {translatedToUnit}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversionTable.map((entry, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: "center", padding: "10px" }}>
                          إن كل
                          {' '}
                          {entry.fromAmount.toLocaleString()}
                          {' '}
                           {translatedFromUnit}
                           {' '}
                          تساوي
                        </td>
                        <td style={{ textAlign: "center", padding: "10px" }}>
                          {formatNumber(entry.toAmount)}
                           {' '} ({translatedToUnit})
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <hr />
            <div
              className="alert alert-info"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              💡 <strong>نصيحة:</strong> يمكنك أيضاً{" "}
              <Link to={`/calculator/ar/unit_converter/weight/`}>
                استعراض جميع تحويلات الوزن المتاحة
              </Link>{" "}
              أو{" "}
              <Link
                to={`/calculator/ar/unit_converter/weight/${toWeightUnit}/${fromWeightUnit}/`}
              >
                عكس التحويل من {translatedToUnit} إلى {translatedFromUnit}
              </Link>
              .
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Educational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>
                  كيف تحول من {translatedFromUnit} إلى {translatedToUnit}؟
                </strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  تحويل <strong>{translatedFromUnit}</strong> إلى{" "}
                  <strong>{translatedToUnit}</strong> هو عملية بسيطة باستخدام معامل
                  التحويل الصحيح.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  معادلة التحويل:
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <strong>القيمة بالـ {translatedToUnit}</strong> = القيمة بالـ{" "}
                  {translatedFromUnit} × {rate ? formatNumber(rate) : "..."}
                  <br/>                  <strong>أو بصيغة أخرى:</strong>
                  <br />1 {translatedFromUnit} = {rate ? formatNumber(rate) : "..."}{" "}
                  {translatedToUnit}
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  أمثلة عملية:
                </h3>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  <strong>مثال 1:</strong> تحويل 10 {translatedFromUnit} إلى{" "}
                  {translatedToUnit}
                  <br />
                  10 × {rate ? formatNumber(rate) : "..."} = {rate ? formatNumber(10 * rate) : "..."}{" "}
                  {translatedToUnit}
                  <br/>                  <strong>مثال 2:</strong> تحويل 50 {translatedFromUnit} إلى{" "}
                  {translatedToUnit}
                  <br />
                  50 × {rate ? formatNumber(rate) : "..."} = {rate ? formatNumber(50 * rate) : "..."}{" "}
                  {translatedToUnit}
                  <br/>                  <strong>مثال 3:</strong> تحويل 100 {translatedFromUnit} إلى{" "}
                  {translatedToUnit}
                  <br />
                  100 × {rate ? formatNumber(rate) : "..."} = {rate ? formatNumber(100 * rate) : "..."}{" "}
                  {translatedToUnit}
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  الطريقة السريعة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>استخدم الآلة الحاسبة أو المحول أعلاه</li>
                  <li>
                    اضرب القيمة بالـ {translatedFromUnit} في {rate ? formatNumber(rate) : "معامل التحويل"}
                  </li>
                  <li>
                    النتيجة ستكون القيمة بالـ {translatedToUnit}
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>
                  معلومات عن {translatedFromUnit} و{translatedToUnit}
                </strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  <strong>عن {translatedFromUnit}:</strong>
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  {fromWeightUnit === "kg" && (
                    <>
                      <strong>الكيلوغرام (كغ)</strong> هو الوحدة الأساسية للوزن في
                      النظام المتري العالمي. يستخدم في معظم دول العالم لقياس
                      الوزن والكتلة.
                      <ul style={{ marginTop: "10px" }}>
                        <li>1 كيلوغرام = 1000 غرام</li>
                        <li>1 كيلوغرام = 2.20462 رطل</li>
                        <li>يستخدم لقياس وزن الجسم، الطعام، البضائع</li>
                      </ul>
                    </>
                  )}
                  {fromWeightUnit === "g" && (
                    <>
                      <strong>الغرام (غ)</strong> هو وحدة أصغر من الكيلوغرام في
                      النظام المتري. يستخدم لقياس الأوزان الخفيفة.
                      <ul style={{ marginTop: "10px" }}>
                        <li>1000 غرام = 1 كيلوغرام</li>
                        <li>1 غرام = 1000 ميليغرام</li>
                        <li>يستخدم في الطبخ، الصيدلة، المواد الخفيفة</li>
                      </ul>
                    </>
                  )}
                  {fromWeightUnit === "pound" && (
                    <>
                      <strong>الرطل (lb)</strong> هو وحدة الوزن الأساسية في النظام
                      الإمبراطوري المستخدم في أمريكا وبريطانيا.
                      <ul style={{ marginTop: "10px" }}>
                        <li>1 رطل = 0.453592 كيلوغرام</li>
                        <li>1 رطل = 16 أوقية</li>
                        <li>يستخدم لقياس وزن الجسم والبضائع في أمريكا</li>
                      </ul>
                    </>
                  )}
                  {fromWeightUnit === "ounce" && (
                    <>
                      <strong>الأوقية (oz)</strong> هي وحدة أصغر من الرطل في النظام
                      الإمبراطوري.
                      <ul style={{ marginTop: "10px" }}>
                        <li>16 أوقية = 1 رطل</li>
                        <li>1 أوقية = 28.3495 غرام</li>
                        <li>تستخدم في الطبخ والوصفات</li>
                      </ul>
                    </>
                  )}
                  {fromWeightUnit === "mg" && (
                    <>
                      <strong>الميليغرام (ملغ)</strong> هو وحدة صغيرة جداً تستخدم
                      للمواد الدقيقة.
                      <ul style={{ marginTop: "10px" }}>
                        <li>1000 ميليغرام = 1 غرام</li>
                        <li>1,000,000 ميليغرام = 1 كيلوغرام</li>
                        <li>تستخدم في جرعات الأدوية والفيتامينات</li>
                      </ul>
                    </>
                  )}
                  {!["kg", "g", "pound", "ounce", "mg"].includes(
                    fromWeightUnit
                  ) && (
                    <>
                      <strong>{translatedFromUnit}</strong> هي وحدة وزن تستخدم في
                      تطبيقات محددة.
                    </>
                  )}
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  <strong>عن {translatedToUnit}:</strong>
                </h3>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  {toWeightUnit === "kg" && (
                    <>
                      <strong>الكيلوغرام (كغ)</strong> هو الوحدة الأساسية للوزن في
                      النظام المتري العالمي.
                    </>
                  )}
                  {toWeightUnit === "g" && (
                    <>
                      <strong>الغرام (غ)</strong> هو وحدة أصغر من الكيلوغرام،
                      تستخدم للأوزان الخفيفة.
                    </>
                  )}
                  {toWeightUnit === "pound" && (
                    <>
                      <strong>الرطل (lb)</strong> هو الوحدة الأساسية للوزن في
                      النظام الإمبراطوري.
                    </>
                  )}
                  {toWeightUnit === "ounce" && (
                    <>
                      <strong>الأوقية (oz)</strong> وحدة أصغر من الرطل، تستخدم في
                      الطبخ.
                    </>
                  )}
                  {toWeightUnit === "mg" && (
                    <>
                      <strong>الميليغرام (ملغ)</strong> وحدة صغيرة جداً للمواد
                      الدقيقة.
                    </>
                  )}
                  {toWeightUnit === "carat" && (
                    <>
                      <strong>القيراط (ct)</strong> وحدة صغيرة تستخدم للأحجار
                      الكريمة والمجوهرات. 1 قيراط = 0.2 غرام.
                    </>
                  )}
                  {toWeightUnit === "metricton" && (
                    <>
                      <strong>الطن المتري</strong> وحدة كبيرة للأوزان الثقيلة. 1
                      طن متري = 1000 كيلوغرام.
                    </>
                  )}
                  {!["kg", "g", "pound", "ounce", "mg", "carat", "metricton"].includes(
                    toWeightUnit
                  ) && (
                    <>
                      <strong>{translatedToUnit}</strong> هي وحدة وزن تستخدم في
                      تطبيقات محددة.
                    </>
                  )}
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  العلاقة بين الوحدتين:
                </h3>
                <div
                  className="alert alert-warning"
                  style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                >
                  <strong>معامل التحويل:</strong> {rate ? formatNumber(rate) : "جاري التحميل..."}
                  <br/>                  {rate ? (
                    <>
                      هذا يعني أن كل {translatedFromUnit} واحد يساوي{" "}
                      {formatNumber(rate)} {translatedToUnit}.
                      <br/>                      <strong>التحويل العكسي:</strong> كل {translatedToUnit} واحد
                      يساوي {formatNumber(1 / rate)} {translatedFromUnit}.
                    </>
                  ) : (
                    "جاري تحميل معلومات التحويل..."
                  )}
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات عملية للتحويل</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "15px" }}>
                  متى تحتاج لتحويل {translatedFromUnit} إلى {translatedToUnit}؟
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>في الحياة اليومية:</strong>
                  </h4>
                  <ul>
                    <li>عند السفر بين دول تستخدم أنظمة قياس مختلفة</li>
                    <li>عند قراءة وصفات طعام من مصادر مختلفة</li>
                    <li>عند شراء منتجات من متاجر عالمية</li>
                    <li>عند متابعة الوزن الصحي باستخدام معايير مختلفة</li>
                  </ul>
                </div>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>في المجالات المهنية:</strong>
                  </h4>
                  <ul>
                    <li>
                      <strong>الطب والصيدلة:</strong> تحويل جرعات الأدوية بين
                      وحدات مختلفة
                    </li>
                    <li>
                      <strong>الطبخ:</strong> اتباع وصفات من مصادر عالمية
                    </li>
                    <li>
                      <strong>الشحن والنقل:</strong> حساب أوزان البضائع
                    </li>
                    <li>
                      <strong>التجارة الدولية:</strong> توحيد المقاييس
                    </li>
                    <li>
                      <strong>البحث العلمي:</strong> التوحيد القياسي للبيانات
                    </li>
                  </ul>
                </div>
                <div
                  className="alert alert-warning"
                  style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>نصائح مهمة:</strong>
                  </h4>
                  <ul>
                    <li>
                      احفظ معاملات التحويل الشائعة للاستخدام السريع
                    </li>
                    <li>استخدم المحولات الإلكترونية للدقة العالية</li>
                    <li>تحقق من الوحدة المستخدمة قبل إجراء أي عمليات حسابية</li>
                    <li>في المجالات الحساسة (كالطب)، تحقق مرتين من التحويل</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
         </div>
</div>
  );
}
export default WeightConverter;